import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { format, parse } from "date-fns";
import { toast } from "sonner";
import { 
  ArrowLeft, Users, Calendar, Car, Plane, Ship, PartyPopper,
  FileText, Compass, RefreshCw, CheckCircle, Clock, MapPin,
  XCircle, Loader2, KeyRound, MessageCircle, Copy, Check
} from "lucide-react";
import { cn } from "@/lib/utils";
import { getBirdBrainGuests, updateTourReservation } from "@/lib/api";

export default function AdminBirdBrainGuests() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [guests, setGuests] = useState([]);
  const [updatingTour, setUpdatingTour] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success("Code copied!");
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const sendWhatsApp = (guest) => {
    const phone = guest.phone_number?.replace(/\D/g, '') || '';
    const message = encodeURIComponent(
      `Hello ${guest.guest_name}! Welcome to ${guest.cabin_name}.\n\n` +
      `Your reservation code is: ${guest.reservation_code}\n\n` +
      `Use this code at amayascorcovado.com to:\n` +
      `• Make restaurant reservations\n` +
      `• Book tours\n` +
      `• Update your arrival info\n\n` +
      `We look forward to hosting you!`
    );
    
    if (phone) {
      window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
    } else {
      window.open(`https://wa.me/?text=${message}`, '_blank');
    }
  };

  useEffect(() => {
    const role = sessionStorage.getItem("userRole");
    if (role !== "admin") {
      navigate("/admin");
    } else {
      loadData();
    }
  }, [navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await getBirdBrainGuests();
      setGuests(res.data);
    } catch (error) {
      console.error("Error loading guests:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmTour = async (tourId, tourName, guestName) => {
    try {
      setUpdatingTour(tourId);
      await updateTourReservation(tourId, {
        status: "confirmed",
        admin_notes: "Confirmed from BBB Guests page"
      });
      toast.success(`${tourName} confirmed for ${guestName}`);
      loadData();
    } catch (error) {
      console.error("Error confirming tour:", error);
      toast.error("Failed to confirm tour");
    } finally {
      setUpdatingTour(null);
    }
  };

  const handleRejectTour = async (tourId, tourName) => {
    try {
      setUpdatingTour(tourId);
      await updateTourReservation(tourId, {
        status: "rejected",
        admin_notes: "Rejected from BBB Guests page"
      });
      toast.success(`Tour reservation rejected`);
      loadData();
    } catch (error) {
      console.error("Error rejecting tour:", error);
      toast.error("Failed to reject tour");
    } finally {
      setUpdatingTour(null);
    }
  };

  const getArrivalIcon = (method) => {
    switch (method) {
      case "vehicle": return <Car className="w-4 h-4" />;
      case "sansa": return <Plane className="w-4 h-4" />;
      case "boat_shuttle": return <Ship className="w-4 h-4" />;
      default: return <MapPin className="w-4 h-4" />;
    }
  };

  const getDepartureIcon = (method) => {
    switch (method) {
      case "own_arrangements": return <Car className="w-4 h-4" />;
      case "sansa_flight": return <Plane className="w-4 h-4" />;
      case "boat_shuttle": return <Ship className="w-4 h-4" />;
      default: return <MapPin className="w-4 h-4" />;
    }
  };

  const getTourStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-100 text-yellow-800 border-yellow-300",
      confirmed: "bg-green-100 text-green-800 border-green-300",
      rejected: "bg-red-100 text-red-800 border-red-300",
      cancelled: "bg-gray-100 text-gray-800 border-gray-300"
    };
    return styles[status] || styles.pending;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Users className="w-12 h-12 text-primary mx-auto mb-4 animate-pulse" />
          <p className="text-muted-foreground">Loading guests...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <Link 
            to="/admin/dashboard"
            className="inline-flex items-center text-muted-foreground hover:text-primary mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Link>
          <div className="flex justify-between items-start">
            <div>
              <h1 className="font-display text-3xl font-bold text-foreground mb-2">
                Bird Brain Bungalows Guests
              </h1>
              <p className="text-muted-foreground">
                {guests.length} active guest(s) with detailed arrival information
              </p>
            </div>
            <Button onClick={loadData} variant="outline" data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Guest Cards */}
        {guests.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="pt-6 text-center py-12">
              <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No active Bird Brain Bungalows guests found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {guests.map((guest) => (
              <Card 
                key={guest.id} 
                className="bg-card border-border shadow-card"
                data-testid={`guest-card-${guest.id}`}
              >
                <CardContent className="pt-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Left Column - Guest Info */}
                    <div className="space-y-4">
                      {/* Header with name and cabin */}
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-display text-xl font-bold text-foreground">
                            {guest.guest_name}
                          </h3>
                          <p className="text-muted-foreground">
                            Cabin: <span className="font-medium text-foreground">{guest.cabin_name}</span>
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {guest.celebration && (
                            <Badge className="bg-pink-100 text-pink-800 border-pink-300 flex items-center gap-1">
                              <PartyPopper className="w-3 h-3" />
                              {guest.celebration.charAt(0).toUpperCase() + guest.celebration.slice(1)}
                            </Badge>
                          )}
                        </div>
                      </div>

                      {/* Reservation Code & WhatsApp */}
                      {guest.reservation_code && (
                        <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <KeyRound className="w-4 h-4 text-primary" />
                            <span className="text-sm text-muted-foreground">Code:</span>
                            <span className="font-mono font-bold text-primary text-lg">{guest.reservation_code}</span>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => copyCode(guest.reservation_code)}
                              className="h-7 w-7 p-0"
                            >
                              {copiedCode === guest.reservation_code ? (
                                <Check className="w-3 h-3 text-green-600" />
                              ) : (
                                <Copy className="w-3 h-3" />
                              )}
                            </Button>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => sendWhatsApp(guest)}
                            className="bg-green-600 hover:bg-green-700 h-8"
                          >
                            <MessageCircle className="w-4 h-4 mr-1" />
                            WhatsApp
                          </Button>
                        </div>
                      )}

                      {/* Reservation Dates */}
                      <div className="bg-background/50 rounded-lg p-4 space-y-2">
                        <h4 className="font-semibold flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-primary" />
                          Reservation Dates
                        </h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-muted-foreground">Check-in:</span>
                            <p className="font-medium">
                              {guest.arrival_date 
                                ? format(parse(guest.arrival_date, "yyyy-MM-dd", new Date()), "MMM d, yyyy")
                                : "Not specified"
                              }
                            </p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Check-out:</span>
                            <p className="font-medium">
                              {format(parse(guest.departure_date, "yyyy-MM-dd", new Date()), "MMM d, yyyy")}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Arrival Details */}
                      {guest.arrival_method && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <h4 className="font-semibold flex items-center gap-2 text-blue-800 mb-2">
                            {getArrivalIcon(guest.arrival_method)}
                            Arrival Plan
                          </h4>
                          <p className="text-sm text-blue-700">{guest.arrival_details}</p>
                        </div>
                      )}

                      {/* Departure Details */}
                      {guest.departure_method && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                          <h4 className="font-semibold flex items-center gap-2 text-amber-800 mb-2">
                            {getDepartureIcon(guest.departure_method)}
                            Departure Plan
                          </h4>
                          <p className="text-sm text-amber-700">{guest.departure_details}</p>
                          {guest.checkout_time && (
                            <p className="text-xs text-amber-600 mt-1">
                              Checkout time: {guest.checkout_time}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Guest Count & Dietary */}
                      <div className="text-sm space-y-1">
                        <p>
                          <span className="text-muted-foreground">Guests:</span>{" "}
                          <span className="font-medium">{guest.guest_count}</span>
                        </p>
                        {guest.dietary_requirements && Object.values(guest.dietary_requirements).some(v => v > 0) && (
                          <p>
                            <span className="text-muted-foreground">Dietary:</span>{" "}
                            <span className="font-medium">
                              {Object.entries(guest.dietary_requirements)
                                .filter(([, count]) => count > 0)
                                .map(([type, count]) => `${count} ${type}`)
                                .join(", ")}
                            </span>
                          </p>
                        )}
                        {guest.special_requests && (
                          <p>
                            <span className="text-muted-foreground">Special Requests:</span>{" "}
                            <span className="font-medium">{guest.special_requests}</span>
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Right Column - Tours & Passports */}
                    <div className="space-y-4">
                      {/* Passports */}
                      <div className="bg-background/50 rounded-lg p-4">
                        <h4 className="font-semibold flex items-center gap-2 mb-2">
                          <FileText className="w-4 h-4 text-primary" />
                          Passports Uploaded
                        </h4>
                        {guest.passport_count > 0 ? (
                          <Badge className="bg-green-100 text-green-800 border-green-300">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            {guest.passport_count} passport(s) uploaded
                          </Badge>
                        ) : (
                          <p className="text-sm text-muted-foreground">No passports uploaded yet</p>
                        )}
                      </div>

                      {/* Tours */}
                      <div className="bg-background/50 rounded-lg p-4">
                        <h4 className="font-semibold flex items-center gap-2 mb-3">
                          <Compass className="w-4 h-4 text-primary" />
                          Tours Requested
                        </h4>
                        {guest.tours.length > 0 ? (
                          <div className="space-y-2">
                            {guest.tours.map((tour, idx) => (
                              <div 
                                key={idx}
                                className="bg-white border border-border rounded-lg p-3"
                              >
                                <div className="flex justify-between items-start mb-1">
                                  <p className="font-medium">{tour.tour_name}</p>
                                  <Badge className={cn("border text-xs", getTourStatusBadge(tour.status))}>
                                    {tour.status.charAt(0).toUpperCase() + tour.status.slice(1)}
                                  </Badge>
                                </div>
                                <div className="text-sm text-muted-foreground space-y-1">
                                  <p>
                                    <Calendar className="w-3 h-3 inline mr-1" />
                                    {format(parse(tour.date, "yyyy-MM-dd", new Date()), "MMM d, yyyy")}
                                  </p>
                                  <p>
                                    <Users className="w-3 h-3 inline mr-1" />
                                    {tour.num_participants} participant(s)
                                  </p>
                                  {tour.pickup_time && (
                                    <p>
                                      <Clock className="w-3 h-3 inline mr-1" />
                                      Pickup: {tour.pickup_time}
                                    </p>
                                  )}
                                  {tour.deposit_confirmed && (
                                    <p className="text-green-600">
                                      <CheckCircle className="w-3 h-3 inline mr-1" />
                                      Deposit confirmed
                                    </p>
                                  )}
                                </div>
                                {/* Confirm/Reject buttons for pending tours */}
                                {tour.status === "pending" && (
                                  <div className="flex gap-2 mt-3 pt-2 border-t border-border">
                                    <Button
                                      size="sm"
                                      onClick={() => handleConfirmTour(tour.id, tour.tour_name, guest.guest_name)}
                                      disabled={updatingTour === tour.id}
                                      className="bg-green-600 hover:bg-green-700 h-7 text-xs"
                                      data-testid={`confirm-tour-${tour.id}`}
                                    >
                                      {updatingTour === tour.id ? (
                                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                      ) : (
                                        <CheckCircle className="w-3 h-3 mr-1" />
                                      )}
                                      Confirm
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="destructive"
                                      onClick={() => handleRejectTour(tour.id, tour.tour_name)}
                                      disabled={updatingTour === tour.id}
                                      className="h-7 text-xs"
                                      data-testid={`reject-tour-${tour.id}`}
                                    >
                                      <XCircle className="w-3 h-3 mr-1" />
                                      Reject
                                    </Button>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">No tours requested yet</p>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
